import { OnScreenKeyboard } from '../../components/OnScreenKeyboard';
import { Tile } from '../../components/Tile';
import type { BoardProps, InputKey } from '../../engine/types';
import { cx } from '../../utils/classnames';
import { changedIndex, type TrellisMove, type TrellisState } from './logic';

export function TrellisBoard({ state, status, dispatch, feedback }: BoardProps<TrellisState, TrellisMove>) {
  const playing = status === 'playing';
  const lastError = feedback?.tone === 'error' ? feedback.nonce : 0;
  const rungsLeft = state.maxRungs - (state.path.length - 1);

  const onKey = (key: InputKey) => {
    if (key.type === 'char') dispatch({ type: 'add', ch: key.value });
    else if (key.type === 'backspace') dispatch({ type: 'remove' });
    else dispatch({ type: 'submit' });
  };

  return (
    <div className="cv-board">
      <div className="cv-trellis">
        <div className="cv-trellis__goal">
          Reach <strong>{state.target.toUpperCase()}</strong>
          <span className="cv-faint"> · {rungsLeft} rung{rungsLeft === 1 ? '' : 's'} left</span>
        </div>

        <div className="cv-trellis__ladder">
          {state.path.map((word, idx) => {
            const ci = idx > 0 ? changedIndex(state.path[idx - 1], word) : -1;
            const reachedTarget = word === state.target;
            return (
              <div className="cv-trellis__rung" key={idx}>
                {word.split('').map((ch, i) => (
                  <Tile
                    key={i}
                    letter={ch}
                    state={reachedTarget || i === ci ? 'correct' : undefined}
                    size="md"
                  />
                ))}
              </div>
            );
          })}

          {playing && (
            <div
              key={`cur-${lastError}`}
              className={cx('cv-trellis__rung', lastError && 'cv-anim-shake')}
            >
              {Array.from({ length: state.length }, (_, i) => (
                <Tile key={i} letter={state.current[i]} filled={i < state.current.length} size="md" />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="cv-board__status" data-tone={feedback?.tone === 'error' ? 'error' : undefined} aria-hidden="true">
        {feedback?.message ?? ''}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {feedback?.message}
      </p>

      <OnScreenKeyboard onKey={onKey} disabled={!playing} />
    </div>
  );
}
